import { graphql } from 'gatsby';
import React, { useEffect, useRef } from 'react';

import CareersInteractiveHeader from '../components/Careers/CareersInteractiveHeader';
import PhotoReel from '../components/Careers/PhotoReel';
import { ArrowLink } from '../components/Common/Links';
import ScrollTo, { smoothScrollToRef } from '../components/Common/ScrollTo';
import Layout from '../components/Layouts/Layout';
import MarketingSeo from '../components/MarketingSeo';
import JobPostings from '../components/PageSections/JobPostings';
import PageSection from '../components/PageSections/PageSection';
import ThreeUp from '../components/PageSections/ThreeUp';
import * as styles from './careers.module.scss';

const Careers = ({ data: { page }, pageContext, location }) => {
  const scrollRef = useRef();
  const containerRef = useRef();

  const onCta = (e) => {
    e.preventDefault();
    smoothScrollToRef(scrollRef, null, 84);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const ashbyId = params.get('ashby_jid');

    // Prevent redirect loop using marker param
    const redirected = params.has('redirected');

    if (ashbyId && !redirected) {
      // Append a flag to avoid future redirection
      params.set('redirected', '1');

      window.location = `/careers/jobs/${ashbyId}?${params.toString()}`;
    }
  }, []);

  const sections = page.pageSections?.map((section, i) => {
    let jobBoardUsed = false;

    switch (section.__typename) {
      case 'ContentfulSectionGroupedItems': {
        return (
          <ThreeUp
            animate={true}
            section={section}
            key={i}
            className={section.backgroundColor === 'White' ? styles.benefits : styles.stats}
          />
        );
      }

      case 'ContentfulComponentMediaGroup': {
        return <PhotoReel key={i} images={section.media} />;
      }

      // NOTE: Keeping just in case we decide to use again...
      // case 'ContentfulComponentSimpleContent': {
      //   return <SlackChannels key={i} section={section} />;
      // }

      case 'ContentfulSectionContentSection': {
        if (jobBoardUsed) {
          break;
        }
        jobBoardUsed = true;
        return (
          <div key={i}>
            <ScrollTo reference={scrollRef} />
            <PageSection className={styles.jobs} data-analytics-section={'job-postings'}>
              <JobPostings heading={section.title} location={location} />
            </PageSection>
          </div>
        );
      }

      default:
        return null;
    }
    return null;
  });

  return (
    <Layout isDach={pageContext.isDach}>
      <div ref={containerRef} className={styles.wrapper}>
        <PageSection
          isTopSection={true}
          backgroundImage={page.headingBackgroundImage}
          className={styles.section}
          data-analytics-section={'careers-hero'}
        >
          <h1 className={styles.heading}>{page.heading}</h1>
          {page.headingCtAs && (
            <ArrowLink
              hoverAnimation={true}
              inheritColor={true}
              label={page.headingCtAs[0].linkText}
              onClick={onCta}
              className={styles.cta}
            />
          )}
        </PageSection>
        <CareersInteractiveHeader containerRef={containerRef} />
      </div>
      {sections}
    </Layout>
  );
};

export const Head = ({ data: { page }, location }) => (
  <MarketingSeo meta={page.pageMetaData} slug={location.pathname} />
);

export default Careers;

export const query = graphql`
  query CareersTemplateQuery($slug: String!) {
    page: contentfulPageStandard(slug: { eq: $slug }) {
      ...StandardPage
    }
  }
`;
